import { useCallback, useEffect, useRef, useState } from "react";
import { Plus, Hash, MessageCircle, Send, Loader2, Users, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import tr from "@/i18n/tr";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";

const inputCls = "h-10 w-full rounded-xl border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand/40";

const fmtWhen = (iso) => {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        const now = new Date();
        const same = d.toDateString() === now.toDateString();
        return same ? d.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" }) : d.toLocaleDateString("tr-TR", { day: "2-digit", month: "short" });
    } catch { return ""; }
};
const initials = (n) => (n || "?").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase();

export default function TeamChatPage() {
    const { user } = useAuth();
    const [channels, setChannels] = useState([]);
    const [dms, setDms] = useState([]);
    const [selected, setSelected] = useState(null); // { type: 'channel'|'dm', id, name }
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [msgLoading, setMsgLoading] = useState(false);
    const [text, setText] = useState("");
    const [sending, setSending] = useState(false);

    // Channel creation
    const [newChOpen, setNewChOpen] = useState(false);
    const [newChName, setNewChName] = useState("");
    const [newChDesc, setNewChDesc] = useState("");

    // DM starter
    const [newDmOpen, setNewDmOpen] = useState(false);
    const [users, setUsers] = useState([]);

    const messagesEndRef = useRef(null);

    const loadSidebar = useCallback(async () => {
        try {
            const [ch, dm] = await Promise.all([
                api.get("/team-chat/channels"),
                api.get("/team-chat/dms"),
            ]);
            setChannels(ch.data.items || []);
            setDms(dm.data.items || []);
            if (!selected && ch.data.items?.length) {
                setSelected({ type: "channel", id: ch.data.items[0].id, name: ch.data.items[0].name });
            }
        } catch (err) { toast.error(err?.response?.data?.detail || "Yüklenemedi."); }
        finally { setLoading(false); }
    }, [selected]);
    useEffect(() => { loadSidebar(); /* eslint-disable-next-line */ }, []);

    const loadMessages = useCallback(async () => {
        if (!selected) return;
        setMsgLoading(true);
        try {
            const url = selected.type === "channel"
                ? `/team-chat/channels/${selected.id}/messages`
                : `/team-chat/dms/${selected.id}/messages`;
            const { data } = await api.get(url);
            setMessages(data.items || []);
            setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }), 60);
        } catch (err) { toast.error(err?.response?.data?.detail || "Yüklenemedi."); }
        finally { setMsgLoading(false); }
    }, [selected]);
    useEffect(() => { loadMessages(); }, [loadMessages]);

    // Poll every 5s for new messages in current conversation
    useEffect(() => {
        if (!selected) return;
        const iv = setInterval(async () => {
            if (!messages.length) return;
            const lastAt = messages[messages.length - 1].at;
            const url = selected.type === "channel"
                ? `/team-chat/channels/${selected.id}/messages`
                : `/team-chat/dms/${selected.id}/messages`;
            try {
                const { data } = await api.get(url, { params: { since: lastAt } });
                if (data.items?.length) {
                    setMessages((prev) => [...prev, ...data.items]);
                    setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }), 60);
                }
            } catch (err) { console.debug("silent:", err); }
        }, 5000);
        return () => clearInterval(iv);
    }, [selected, messages]);

    const send = async (e) => {
        e.preventDefault();
        if (!text.trim() || !selected) return;
        setSending(true);
        const t = text.trim(); setText("");
        try {
            const url = selected.type === "channel"
                ? `/team-chat/channels/${selected.id}/messages`
                : `/team-chat/dms/${selected.id}/messages`;
            const { data } = await api.post(url, { text: t });
            setMessages((prev) => [...prev, data]);
            setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }), 60);
        } catch (err) { toast.error(err?.response?.data?.detail || "Gönderilemedi."); setText(t); }
        finally { setSending(false); }
    };

    const createChannel = async (e) => {
        e.preventDefault();
        if (!newChName.trim()) return toast.error("Kanal adı zorunlu.");
        try {
            const { data } = await api.post("/team-chat/channels", { name: newChName, description: newChDesc });
            setChannels((prev) => [...prev, data]);
            setSelected({ type: "channel", id: data.id, name: data.name });
            setNewChOpen(false); setNewChName(""); setNewChDesc("");
            toast.success("Kanal oluşturuldu.");
        } catch (err) { toast.error(err?.response?.data?.detail || "Oluşturulamadı."); }
    };

    const openDmModal = async () => {
        setNewDmOpen(true);
        try {
            const { data } = await api.get("/team-chat/users");
            setUsers(data.items || []);
        } catch (err) { console.debug("silent:", err); }
    };
    const startDm = async (uid) => {
        try {
            const { data } = await api.post("/team-chat/dms", { other_user_id: uid });
            setNewDmOpen(false);
            // refresh sidebar so this DM appears if new
            await loadSidebar();
            const other = users.find(u => u.id === uid);
            setSelected({ type: "dm", id: data.id, name: other?.full_name || "DM" });
        } catch (err) { toast.error(err?.response?.data?.detail || "Başlatılamadı."); }
    };

    if (loading) return <div className="flex items-center justify-center py-16 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Yükleniyor…</div>;

    return (
        <div className="animate-fade-in">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
                <div>
                    <h1 className="text-[26px] font-bold tracking-tight" data-testid="teamchat-title">Personel Sohbeti</h1>
                    <p className="text-[13.5px] text-muted-foreground mt-1">Kanal bazlı grup sohbeti ve birebir DM. 5 saniyede bir otomatik yenilenir.</p>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-4" style={{ minHeight: "600px" }}>
                {/* Sidebar */}
                <div className="card-elevated p-3 space-y-3">
                    <div>
                        <div className="flex items-center justify-between mb-1">
                            <div className="text-[11px] font-bold uppercase text-muted-foreground">Kanallar</div>
                            <button onClick={() => setNewChOpen(true)} className="inline-flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-[hsl(var(--brand))]" title="Yeni Kanal" data-testid="btn-new-channel"><Plus className="h-3.5 w-3.5" /></button>
                        </div>
                        <ul className="space-y-0.5">
                            {channels.map((c) => (
                                <li key={c.id}>
                                    <button onClick={() => setSelected({ type: "channel", id: c.id, name: c.name })}
                                        className={`w-full flex items-center gap-2 rounded-lg px-2 py-1.5 text-[13px] font-semibold text-left hover:bg-muted ${selected?.type === "channel" && selected.id === c.id ? "bg-muted" : ""}`}
                                        data-testid={`channel-item-${c.slug}`}>
                                        <Hash className="h-3.5 w-3.5 text-muted-foreground" />
                                        <span className="flex-1 truncate">{c.name}</span>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div>
                        <div className="flex items-center justify-between mb-1">
                            <div className="text-[11px] font-bold uppercase text-muted-foreground">Direkt Mesajlar</div>
                            <button onClick={openDmModal} className="inline-flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-[hsl(var(--brand))]" title="Yeni DM" data-testid="btn-new-dm"><Plus className="h-3.5 w-3.5" /></button>
                        </div>
                        <ul className="space-y-0.5">
                            {dms.length === 0 && <li className="text-[11.5px] text-muted-foreground px-2 py-1">Henüz DM yok.</li>}
                            {dms.map((d) => (
                                <li key={d.id}>
                                    <button onClick={() => setSelected({ type: "dm", id: d.id, name: d.other?.full_name || "DM" })}
                                        className={`w-full flex items-center gap-2 rounded-lg px-2 py-1.5 text-[13px] font-semibold text-left hover:bg-muted ${selected?.type === "dm" && selected.id === d.id ? "bg-muted" : ""}`}
                                        data-testid={`dm-item-${d.id}`}>
                                        <div className="flex h-6 w-6 items-center justify-center rounded-full bg-[hsl(var(--brand))]/10 text-[hsl(var(--brand))] text-[10px] font-bold">{initials(d.other?.full_name)}</div>
                                        <span className="flex-1 truncate">{d.other?.full_name || "—"}</span>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                {/* Conversation panel */}
                <div className="card-elevated flex flex-col" style={{ minHeight: "600px" }}>
                    {!selected ? (
                        <div className="flex-1 flex items-center justify-center text-muted-foreground">Sohbet seçin.</div>
                    ) : (
                        <>
                            <div className="border-b border-border px-4 py-3 flex items-center gap-2">
                                {selected.type === "channel" ? <Hash className="h-4 w-4 text-muted-foreground" /> : <MessageCircle className="h-4 w-4 text-muted-foreground" />}
                                <div className="text-[14px] font-bold" data-testid="conv-title">{selected.name}</div>
                            </div>
                            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
                                {msgLoading && <div className="text-center text-muted-foreground py-6"><Loader2 className="h-5 w-5 animate-spin inline-block mr-2" /> Yükleniyor…</div>}
                                {!msgLoading && messages.length === 0 && <div className="text-center text-muted-foreground py-10 text-[13px]">Henüz mesaj yok. İlk mesajı siz gönderin.</div>}
                                {messages.map((m) => {
                                    const mine = m.author_id === user?.id;
                                    return (
                                        <div key={m.id} className={`flex gap-2 ${mine ? "flex-row-reverse" : ""}`} data-testid={`msg-${m.id}`}>
                                            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--brand))]/10 text-[hsl(var(--brand))] text-[11px] font-bold">{initials(m.author_name)}</div>
                                            <div className={`max-w-[70%] ${mine ? "text-right" : ""}`}>
                                                <div className="text-[10.5px] text-muted-foreground">{m.author_name} • {fmtWhen(m.at)}</div>
                                                <div className={`inline-block rounded-2xl px-3 py-1.5 text-[13.5px] whitespace-pre-wrap ${mine ? "bg-[hsl(var(--brand))] text-white" : "bg-muted"}`}>{m.text}</div>
                                            </div>
                                        </div>
                                    );
                                })}
                                <div ref={messagesEndRef} />
                            </div>
                            <form onSubmit={send} className="border-t border-border p-3 flex gap-2">
                                <input placeholder={`${selected.type === "channel" ? "#" + selected.name : selected.name} kısmına mesaj yazın…`} className={`${inputCls} flex-1`} value={text} onChange={(e) => setText(e.target.value)} data-testid="conv-input" />
                                <button type="submit" disabled={sending} className="btn-brand h-10" data-testid="conv-send"><Send className="h-4 w-4" /> Gönder</button>
                            </form>
                        </>
                    )}
                </div>
            </div>

            {/* New Channel Modal */}
            <Dialog open={newChOpen} onOpenChange={(o) => !o && setNewChOpen(false)}>
                <DialogContent className="sm:max-w-md rounded-2xl">
                    <DialogHeader><DialogTitle>Yeni Kanal</DialogTitle></DialogHeader>
                    <form onSubmit={createChannel} className="space-y-3 pt-2">
                        <input required placeholder="Kanal adı (örn. Video Ekibi)" className={inputCls} value={newChName} onChange={(e) => setNewChName(e.target.value)} data-testid="new-channel-name" />
                        <input placeholder="Açıklama (opsiyonel)" className={inputCls} value={newChDesc} onChange={(e) => setNewChDesc(e.target.value)} />
                        <DialogFooter>
                            <button type="button" onClick={() => setNewChOpen(false)} className="inline-flex items-center justify-center rounded-xl border border-border bg-background px-4 py-2.5 text-sm font-semibold hover:bg-muted">{tr.common.cancel}</button>
                            <button type="submit" className="btn-brand" data-testid="new-channel-save">Oluştur</button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* New DM Modal */}
            <Dialog open={newDmOpen} onOpenChange={(o) => !o && setNewDmOpen(false)}>
                <DialogContent className="sm:max-w-md rounded-2xl">
                    <DialogHeader><DialogTitle>Yeni Direkt Mesaj</DialogTitle></DialogHeader>
                    <div className="space-y-1.5 pt-2 max-h-[400px] overflow-y-auto">
                        {users.length === 0 && <div className="text-[12.5px] text-muted-foreground py-6 text-center">Kullanıcı yok.</div>}
                        {users.map((u) => (
                            <button key={u.id} onClick={() => startDm(u.id)} className="w-full flex items-center gap-2 rounded-lg px-2 py-2 text-left hover:bg-muted" data-testid={`dm-start-${u.id}`}>
                                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[hsl(var(--brand))]/10 text-[hsl(var(--brand))] text-[11px] font-bold">{initials(u.full_name)}</div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-[13px] font-semibold truncate">{u.full_name}</div>
                                    <div className="text-[11px] text-muted-foreground truncate">{u.role} • {u.department || "—"}</div>
                                </div>
                            </button>
                        ))}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
